<template>
  <div>
    <div class="details-stack">
      <div class="doc-field">
        <div class="doc-header">Driving License / Passport (Front)</div>
        <input
          ref="idFrontInput"
          type="file"
          class="file-input"
          accept="image/*"
          @change="onChange($event, 'id_front')"
        />
        <v-btn variant="outlined" prepend-icon="mdi-upload" @click="pick('id_front')">
          Choose File
        </v-btn>
        <div class="text-center">
          <img v-if="previews.id_front" :src="previews.id_front" class="file-preview" />
          <div class="file-name" :class="{ 'has-error': getError('id_front') }">
            {{ id_front?.name || "No file selected" }}
          </div>
        </div>
        <div v-if="getError('id_front')" class="field-error">
          {{ getError("id_front") }}
        </div>
      </div>

      <div class="doc-field">
        <div class="doc-header">Driving License / Passport (Back)</div>
        <input
          ref="idBackInput"
          type="file"
          class="file-input"
          accept="image/*"
          @change="onChange($event, 'id_back')"
        />
        <v-btn variant="outlined" prepend-icon="mdi-upload" @click="pick('id_back')">
          Choose File
        </v-btn>
        <div class="text-center">
          <img v-if="previews.id_back" :src="previews.id_back" class="file-preview" />
          <div class="file-name" :class="{ 'has-error': getError('id_back') }">
            {{ id_back?.name || "No file selected" }}
          </div>
        </div>
        <div v-if="getError('id_back')" class="field-error">
          {{ getError("id_back") }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onBeforeUnmount, toRefs } from "vue";

type DocumentKey = "id_front" | "id_back";

const idFrontInput = ref<HTMLInputElement | null>(null);
const idBackInput = ref<HTMLInputElement | null>(null);

const props = withDefaults(
  defineProps<{
    id_front: File | null;
    id_back: File | null;
    errors: {
      id_front?: string;
      id_back?: string;
    };
  }>(),
  {
    id_front: null,
    id_back: null,
    errors: () => ({}),
  }
);

const { id_front, id_back, errors } = toRefs(props);

const emit = defineEmits<{
  "update:id_front": [File | null];
  "update:id_back": [File | null];
}>();

// Local client-side errors (e.g., invalid file type)
const localErrors = ref<{ id_front?: string; id_back?: string }>({});

// Object URL previews for image files
const previews = ref<{ id_front: string | null; id_back: string | null }>({
  id_front: null,
  id_back: null,
});

function getError(key: DocumentKey) {
  // prefer local validation errors, fall back to parent-provided errors
  return localErrors.value[key] || (errors.value && (errors.value as any)[key]);
}

function isImageFile(file: File) {
  // Some browsers can return an empty mime type; fall back to extension.
  if (file.type) return file.type.startsWith("image/");
  return /\.(png|jpe?g|webp|gif|bmp|heic|heif)$/i.test(file.name);
}

function setPreview(key: DocumentKey, file: File | null) {
  const current = previews.value[key];
  if (current) URL.revokeObjectURL(current);
  previews.value[key] = file ? URL.createObjectURL(file) : null;
}

function pick(key: DocumentKey) {
  if (key === "id_front") idFrontInput.value?.click();
  if (key === "id_back") idBackInput.value?.click();
}

function onChange(event: Event, key: DocumentKey) {
  const input = event.target as HTMLInputElement | null;
  const file = input?.files?.[0] || null;

  if (file && !isImageFile(file)) {
    // reject non-image files
    localErrors.value[key] = "Only image files are allowed (jpg, png, gif, etc.)";
    // clear selection in input
    if (input) input.value = "";
    // inform parent we didn't accept the file
    if (key === "id_front") emit("update:id_front", null);
    if (key === "id_back") emit("update:id_back", null);
    setPreview(key, null);
    return;
  }

  // clear any previous local error
  localErrors.value[key] = undefined;

  // emit the chosen file (may be null)
  if (key === "id_front") emit("update:id_front", file);
  if (key === "id_back") emit("update:id_back", file);

  // update preview immediately (don't wait on parent prop update)
  setPreview(key, file);

  // clear native input so same file can be re-selected later
  if (input) input.value = "";
}

// Create and manage previews when parent updates the files
watch(id_front, (newVal) => {
  if (newVal && isImageFile(newVal as File)) setPreview("id_front", newVal as File);
  else setPreview("id_front", null);
});

watch(id_back, (newVal) => {
  if (newVal && isImageFile(newVal as File)) setPreview("id_back", newVal as File);
  else setPreview("id_back", null);
});

onBeforeUnmount(() => {
  if (previews.value.id_front) URL.revokeObjectURL(previews.value.id_front);
  if (previews.value.id_back) URL.revokeObjectURL(previews.value.id_back);
});
</script>

<style scoped>
.section-title {
  text-transform: capitalize;
}

.section-subtitle {
  margin: 0 0 14px;
  color: #73829a;
  font-size: 14px;
}

.details-stack {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.doc-field {
  border: 1px solid var(--border-color);
  border-radius: 12px;
  background: #fff;
  padding: 12px 18px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.doc-header {
  color: #1f2430;
  font-size: 14px;
  font-weight: 700;
}

.file-input {
  display: none;
}

.file-name {
  color: #5b677a;
  font-size: 13px;
}

.file-name.has-error {
  color: #b42318;
}

.field-error {
  font-size: 12px;
  color: #b42318;
}

.file-preview {
  width: 100%;
  max-width: 320px;
  height: auto;
  border-radius: 10px;
  border: 1px solid var(--border-color);
  background: #fff;
}

.file-preview {
  max-width: 160px;
  max-height: 120px;
  object-fit: contain;
  border-radius: 8px;
  border: 1px solid #e6e9ef;
  margin-top: 8px;
}
</style>
